import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Card, Chip, Collapse, Divider, ListItemButton, ListItemText, Stack, Typography } from '@mui/material'
import { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { useState } from 'react'
import { Control, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import type { PayoutLineSchemaType, PayoutSchemaType } from './loan-form-schema'
import PayoutDialog from './PayoutDialog'
import PayoutLineList from './PayoutLineList'

interface Props<TFieldValues extends FieldValues> {
  payout: PayoutSchemaType
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, PayoutLineSchemaType>
  onDelete: () => void
  onUpdate: (data: PayoutSchemaType) => void
}

const PayoutItem = <TFieldValues extends FieldValues = FieldValues>({
  payout,
  control,
  name,
  onDelete,
  onUpdate,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const [openCollapse, setOpenCollapse] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)

  const onProductLineAdded = (data: PayoutLineSchemaType) => {
    payout.lines.push(data)
  }

  const onProductLineUpdated = (data: PayoutLineSchemaType, index: number) => {
    payout.lines[index] = data
  }

  const onProductLineRemoved = (index: number) => {
    payout.lines = payout.lines.filter((_, id) => id !== index)
  }

  return (
    <Card variant="outlined">
      <ListItemButton onClick={() => setOpenCollapse((o) => !o)}>
        <ListItemText
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{payout.payeeId}</Typography>
                <Chip label={t(`enum.targetType.${payout.payeeType}`)} />
                <Chip label={t(`EPaymentMethod.${payout.paymentMethod}`, { ns: 'enum' })} />
              </Stack>
              <div>
                <EditIconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenDialog(true)
                  }}
                />
                <DeleteIconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                />
              </div>
            </Stack>
          }
        />
        {openCollapse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={openCollapse} timeout="auto" unmountOnExit>
        <Divider />
        <PayoutLineList
          control={control}
          name={name}
          onInsert={onProductLineAdded}
          onDelete={onProductLineRemoved}
          onUpdate={onProductLineUpdated}
        />
      </Collapse>
      <PayoutDialog
        open={openDialog}
        payeeType={payout.payeeType}
        onClose={() => setOpenDialog(false)}
        onSubmit={(data) => onUpdate(data as never)}
        defaultValues={payout}
      />
    </Card>
  )
}

export default PayoutItem
